import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { CareShell, Panel } from "../../components/care/CareLayout";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import { careService } from "../../services/careService";
import { titleCase, toList } from "../professionalProfileUtils";

const STAFF_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const initialsFor = (name = "") =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase() || "--";

const profileName = (profile = {}) => {
  const data = profile.profile || profile.profileData || {};
  const user = profile.userProfile || profile.user || {};
  return (
    user.name ||
    profile.name ||
    profile.fullName ||
    data.fullName ||
    [data.firstName, data.middleName, data.lastName].filter(Boolean).join(" ").trim() ||
    profile.userName ||
    profile.userProfileId ||
    "Care staff profile"
  );
};

const careDataFor = (profile = {}) => profile.care || profile.careData || {};
const userFor = (profile = {}) => profile.userProfile || profile.user || {};
const workspaceIdFor = (profile = {}) =>
  profile.workspaceId ||
  profile.wid ||
  profile.workspace?.id ||
  profile.workspace?.workspaceId ||
  profile.care?.workspaceId ||
  profile.careData?.workspaceId ||
  "";

const STATUS_FILTERS = [
  { key: "ALL", label: "All", statuses: [] },
  { key: "IN_PROGRESS", label: "In progress", statuses: ["IN_PROGRESS"] },
  { key: "COMPLETE", label: "Completed", statuses: ["COMPLETE", "COMPLETED"] },
  { key: "ACTIVE", label: "Active", statuses: ["ACTIVE"] },
  { key: "APPROVED", label: "Approved", statuses: ["APPROVED"] },
];

const completedStatuses = ["COMPLETE", "COMPLETED", "ACTIVE", "APPROVED"];

const normalizeCareStaffProfile = (profile = {}) => {
  const care = careDataFor(profile);
  const user = userFor(profile);
  const data = profile.profile || profile.profileData || {};
  const name = profileName(profile);
  return {
    id: profile.id || profile.careProfileId,
    workspaceId: workspaceIdFor(profile),
    userProfileId: user.id || profile.userProfileId || profile.userId,
    professionalProfileId: profile.professionalProfileId,
    employeeNumber: profile.employeeNumber || "",
    name,
    email: user.email || data.email || profile.email || "",
    phone: user.phone || profile.phone || "",
    initials: initialsFor(name),
    status: String(profile.onboardingStatus || profile.status || "IN_PROGRESS").toUpperCase(),
    onboardingStage: profile.onboardingStage || profile.stepKey || "",
    shifts: toList(care.preferredShifts).join(", "),
    workDays: toList(care.canWorkOn).join(", "),
    languages: [...toList(care.languages).filter((item) => item !== "Other"), care.otherLanguage].filter(Boolean).join(", "),
    commuteDistance: care.travelRadius || "",
    updatedAt: profile.updatedAt || profile.createdAt || "",
  };
};

const statusPillClass = (status = "") =>
  completedStatuses.includes(status)
    ? "green"
    : status === "IN_PROGRESS"
      ? "blue"
      : "medium";

export default function CareStaffProfilesPage() {
  const { selectedWorkspaceId } = useWorkspace();
  const [profiles, setProfiles] = useState([]);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    if (!selectedWorkspaceId) {
      setProfiles([]);
      setError("");
      setLoading(false);
      return () => {
        active = false;
      };
    }
    setLoading(true);
    careService.careProfiles.list({ workspaceId: selectedWorkspaceId, wid: selectedWorkspaceId })
      .then((data) => {
        if (!active) return;
        const workspaceProfiles = (Array.isArray(data) ? data : [])
          .filter((profile) => workspaceIdFor(profile) === selectedWorkspaceId);
        setProfiles(workspaceProfiles.map(normalizeCareStaffProfile));
        setError("");
      })
      .catch((err) => {
        if (!active) return;
        setProfiles([]);
        setError(err?.response?.data?.message || err?.message || "Unable to load care staff profiles.");
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [selectedWorkspaceId]);

  const completed = useMemo(
    () => profiles.filter((profile) => completedStatuses.includes(profile.status)).length,
    [profiles],
  );
  const inProgress = profiles.filter((profile) => profile.status === "IN_PROGRESS").length;
  const filteredProfiles = useMemo(
    () => {
      const filter = STATUS_FILTERS.find((item) => item.key === statusFilter);
      if (!filter || !filter.statuses.length) return profiles;
      return profiles.filter((profile) => filter.statuses.includes(profile.status));
    },
    [profiles, statusFilter],
  );
  const totalPages = Math.max(1, Math.ceil(filteredProfiles.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paginatedProfiles = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredProfiles.slice(startIndex, startIndex + pageSize);
  }, [currentPage, filteredProfiles, pageSize]);
  const paginationStart = filteredProfiles.length ? ((currentPage - 1) * pageSize) + 1 : 0;
  const paginationEnd = Math.min(filteredProfiles.length, currentPage * pageSize);

  useEffect(() => {
    setPage(1);
  }, [pageSize, statusFilter]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  return (
    <CareShell className="dashboard-content residents-page care-staff-profiles-page">
      <div className="residents-page-head">
        <div className="page-title">
          <h1>Care Staff</h1>
          <p>Caregiver onboarding profiles captured in this workspace.</p>
        </div>
      </div>

      <div className="resident-summary-grid">
        {[
          ["ti-users-group", "Total staff", profiles.length, "From care profiles"],
          ["ti-circle-check", "Completed", completed, "Ready for review or assignment"],
          ["ti-progress", "In progress", inProgress, "Still onboarding"],
          ["ti-id", "Professional profiles", profiles.filter((profile) => profile.professionalProfileId).length, "Linked from Olreadia"],
        ].map(([icon, label, value, note]) => (
          <article className="resident-summary-card" key={label}>
            <span className="tiny-icon"><i className={`ti ${icon}`} /></span>
            <div><strong>{label}</strong><b>{value}</b><small>{note}</small></div>
          </article>
        ))}
      </div>

      <Panel title="Care Staff">
        {error ? <p className="form-error">{error}</p> : null}
        <div className="resident-filter-pills">
          {STATUS_FILTERS.map((filter) => (
            <button
              type="button"
              key={filter.key}
              className={statusFilter === filter.key ? "is-active" : ""}
              onClick={() => setStatusFilter(filter.key)}
            >
              {filter.label}
            </button>
          ))}
        </div>
        <div className="resident-directory">
          <div className="resident-directory-row resident-directory-head">
            <span>Name / email</span><span>Preferred shifts</span><span>Can work on</span><span>Languages</span><span>Commute</span><span>Status</span><span />
          </div>
          {!selectedWorkspaceId ? <p>Select a workspace to view care staff.</p> : null}
          {loading ? <p>Loading care staff profiles...</p> : null}
          {selectedWorkspaceId && !loading && !profiles.length ? <p>No care staff found in this workspace.</p> : null}
          {!loading && profiles.length > 0 && !filteredProfiles.length ? <p>No care staff match this status.</p> : null}
          {paginatedProfiles.map((profile) => {
            const rowPath = profile.id ? `/staff/${encodeURIComponent(profile.id)}` : "/staff";
            return (
              <Link className="resident-directory-row" to={rowPath} key={profile.id || profile.userProfileId || profile.name}>
                <span className="resident-directory-person">
                  <b>{profile.initials}</b>
                  <span>
                    <strong>{profile.name}</strong>
                    <small>{profile.employeeNumber || "Employee number pending"}</small>
                    <small>{profile.email || "No email"}</small>
                  </span>
                </span>
                <span>{profile.shifts || "Not added"}</span>
                <span>{profile.workDays || "Not added"}</span>
                <span>{profile.languages || "Not added"}</span>
                <span>{profile.commuteDistance || "Not added"}</span>
                <span><em className={`pill ${statusPillClass(profile.status)}`}>{titleCase(profile.status || "Unknown")}</em></span>
                <span className="resident-row-actions"><i className="ti ti-chevron-right" /></span>
              </Link>
            );
          })}
        </div>
        {filteredProfiles.length ? (
          <div className="timesheet-pagination staff-pagination">
            <span>
              Showing <strong>{paginationStart}-{paginationEnd}</strong> of <strong>{filteredProfiles.length}</strong>
            </span>
            <label>
              Rows
              <select value={pageSize} onChange={(event) => setPageSize(Number(event.target.value))}>
                {STAFF_PAGE_SIZE_OPTIONS.map((size) => (
                  <option value={size} key={size}>{size}</option>
                ))}
              </select>
            </label>
            <div className="timesheet-pagination-actions">
              <button
                className="mini-btn"
                type="button"
                onClick={() => setPage((current) => Math.max(1, current - 1))}
                disabled={currentPage <= 1}
              >
                <i className="ti ti-chevron-left" /> Previous
              </button>
              <strong>Page {currentPage} of {totalPages}</strong>
              <button
                className="mini-btn"
                type="button"
                onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                disabled={currentPage >= totalPages}
              >
                Next <i className="ti ti-chevron-right" />
              </button>
            </div>
          </div>
        ) : null}
      </Panel>
    </CareShell>
  );
}
